interface Turnstile {
  render(
    container: string,
    options: {
      sitekey: string;
      execution: "execute";
      appearance: "interaction-only";
      callback: (token: string) => void;
      "error-callback": () => void;
    },
  ): string;
  reset(widgetId: string): void;
  execute(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

// `getToken` rejects with this error when the verification fails.
export class TurnstileError extends Error {}

// Renders the widget in #turnstile-widget, and returns a function that gets a new token.
// Each upload or comment uses a new token, because a token is valid one time only.
export function startTurnstile(siteKey: string | Promise<string>): () => Promise<string> {
  let pending: { resolve: (token: string) => void; reject: (error: Error) => void } | null = null;

  async function render(): Promise<{ turnstile: Turnstile; widgetId: string }> {
    while (typeof window.turnstile?.render !== "function") await new Promise((r) => setTimeout(r, 50));
    const turnstile = window.turnstile;
    const widgetId = turnstile.render("#turnstile-widget", {
      sitekey: await siteKey,
      execution: "execute",
      appearance: "interaction-only",
      callback: (token) => pending?.resolve(token),
      "error-callback": () => pending?.reject(new TurnstileError()),
    });
    return { turnstile, widgetId };
  }
  const ready = render();

  return async function getToken() {
    const { turnstile, widgetId } = await ready;
    const token = new Promise<string>((resolve, reject) => {
      pending = { resolve, reject };
    });
    turnstile.reset(widgetId);
    turnstile.execute(widgetId);
    return token;
  };
}

export function alertText(error: unknown): string {
  if (error instanceof TurnstileError) return "The verification failed. Try again.";
  return error instanceof Error ? error.message : String(error);
}
